import { useEffect, useState, useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { colors } from '../styles/tokens'
import StatusBadge from '../components/StatusBadge'
import { EmptyState, ErrorBanner, SkeletonRows } from '../components/Feedback'
import EstimationBreadcrumb from '../components/EstimationBreadcrumb'
import { ProjectContext } from '../context/ProjectContext'
import { useAuth } from '../../context/AuthContext'

export default function TenderListPage() {
  const selectedProject = useContext(ProjectContext)
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [tenders, setTenders] = useState(null)
  const [error, setError] = useState(null)
  // Whether to show the "+ New Tender" button at all -- a UI hint
  // only. The real authorization boundary is the create_tender RPC's
  // own est_has_permission_any_tender('Tender.Create') check; hiding
  // the button here never substitutes for it.
  const [canCreateTender, setCanCreateTender] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', client_name: '', location: '' })
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase.rpc('est_has_permission_any_tender', { p_permission: 'Tender.Create' })
      if (!cancelled && !error) setCanCreateTender(!!data)
    })()
    return () => { cancelled = true }
  }, [])

  async function handleCreateTender(e) {
    e.preventDefault()
    if (!createForm.name.trim()) {
      setCreateError('Tender name is required.')
      return
    }
    setCreating(true)
    setCreateError(null)
    // The ONLY write path -- no direct INSERT into estimation.tender
    // from the frontend. Authorization is enforced inside the RPC.
    const { data: newTenderId, error } = await supabase.rpc('create_tender', {
      p_name: createForm.name.trim(),
      p_client_name: createForm.client_name.trim() || null,
      p_location: createForm.location.trim() || null,
    })
    if (error) {
      setCreateError(error.message)
      setCreating(false)
      return
    }
    setCreating(false)
    navigate(`/estimation/tender/${newTenderId}`)
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      // Pull the latest revision's status/updated_at per tender in one
      // pass so the list can show real workspace context, not just the
      // tender row itself.
      const { data, error } = await supabase
        .from('tender')
        .select('id, name, client_name, location, status, created_at, estimate_revision(id, revision_number, status, updated_at)')
        .order('created_at', { ascending: false })

      if (cancelled) return
      if (error) {
        setError(error.message)
        return
      }

      let rows = data

      // Phase B: when a CostSpace Project is selected, filter to only
      // the Tenders explicitly mapped to it via the approved
      // tender_costspace_project_mapping table -- read-only here, no
      // mapping is created or altered by simply viewing this list. When
      // no Project is selected, behavior is completely unchanged (every
      // Tender the user has access to, as before).
      if (selectedProject?.id) {
        const { data: mappings, error: mapErr } = await supabase
          .from('tender_costspace_project_mapping')
          .select('tender_id')
          .eq('costspace_project_id', selectedProject.id)
        if (cancelled) return
        if (mapErr) {
          setError(mapErr.message)
          return
        }
        const mappedIds = new Set((mappings || []).map((m) => m.tender_id))
        rows = data.filter((t) => mappedIds.has(t.id))
      }

      const withLatestRevision = rows.map((t) => {
        const revisions = t.estimate_revision || []
        const latest = revisions.slice().sort((a, b) => b.revision_number - a.revision_number)[0]
        return { ...t, latestRevision: latest, revisionCount: revisions.length }
      })
      setTenders(withLatestRevision)
    })()
    return () => {
      cancelled = true
    }
  }, [selectedProject?.id])

  return (
    <div>
      <EstimationBreadcrumb />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Tenders</h1>
          <p style={{ fontSize: 13, color: colors.textSecondary, margin: '4px 0 0' }}>
            {selectedProject
              ? `Showing tenders mapped to ${selectedProject.name}.`
              : 'Showing tenders your account has access to.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Cost Code Dictionary / Resource Dictionary links removed --
              per the approved Tender-scoped architecture, both are only
              reachable from inside a specific Tender (see
              TenderWorkspacePage.jsx), never from this global list. A
              global link here would resolve to a route with no
              tenderId and fail. */}
          {profile?.role === 'admin' && (
            <Link to="/estimation/admin/customer-tenders"
              style={{ fontSize: 13, fontWeight: 700, padding: '8px 16px', background: colors.primary, color: '#fff', borderRadius: 6, textDecoration: 'none' }}>
              Customer Tenders (admin)
            </Link>
          )}
          {canCreateTender && (
            <button onClick={() => setShowCreateForm((s) => !s)}
              style={{ fontSize: 13, fontWeight: 700, padding: '8px 16px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              {showCreateForm ? 'Cancel' : '+ New Tender'}
            </button>
          )}
        </div>
      </div>

      {showCreateForm && (
        <form onSubmit={handleCreateTender} style={{ display: 'flex', gap: 10, alignItems: 'end', marginBottom: 20, padding: 16, background: '#F5F1EA', borderRadius: 8 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Name *</label>
            <input required value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              style={{ padding: '7px 10px', border: '1px solid #DDD9D0', borderRadius: 5, fontSize: 13, minWidth: 200 }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Client</label>
            <input value={createForm.client_name} onChange={(e) => setCreateForm((f) => ({ ...f, client_name: e.target.value }))}
              style={{ padding: '7px 10px', border: '1px solid #DDD9D0', borderRadius: 5, fontSize: 13, minWidth: 160 }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Location</label>
            <input value={createForm.location} onChange={(e) => setCreateForm((f) => ({ ...f, location: e.target.value }))}
              style={{ padding: '7px 10px', border: '1px solid #DDD9D0', borderRadius: 5, fontSize: 13, minWidth: 160 }} />
          </div>
          <button type="submit" disabled={creating}
            style={{ padding: '8px 16px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {creating ? 'Creating…' : 'Create'}
          </button>
        </form>
      )}
      <ErrorBanner message={createError} />

      <ErrorBanner message={error} />

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {tenders === null && !error && (
          <div style={{ padding: '8px 20px' }}>
            <SkeletonRows rows={4} cols={5} />
          </div>
        )}

        {tenders?.length === 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState
              title="No tenders yet"
              description="Tenders you're granted access to will appear here."
            />
          </div>
        )}

        {tenders?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                <Th>Tender</Th>
                <Th>Client / Location</Th>
                <Th>Latest Revision</Th>
                <Th>Status</Th>
                <Th align="right">Last Updated</Th>
              </tr>
            </thead>
            <tbody>
              {tenders.map((t) => (
                <tr
                  key={t.id}
                  style={{ borderBottom: `1px solid ${colors.border}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#FAFBFC')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Td>
                    <Link to={`/estimation/tender/${t.id}`} style={{ color: colors.primary, fontWeight: 600, textDecoration: 'none' }}>
                      {t.name}
                    </Link>
                    <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                      {t.revisionCount} revision{t.revisionCount === 1 ? '' : 's'}
                    </div>
                  </Td>
                  <Td>
                    <div>{t.client_name || '—'}</div>
                    <div style={{ fontSize: 12, color: colors.textMuted }}>{t.location || '—'}</div>
                  </Td>
                  <Td>
                    {t.latestRevision ? `Rev ${t.latestRevision.revision_number}` : '—'}
                  </Td>
                  <Td>
                    {t.latestRevision ? <StatusBadge status={t.latestRevision.status} /> : <StatusBadge status={t.status} />}
                  </Td>
                  <Td align="right" muted>
                    {t.latestRevision?.updated_at
                      ? new Date(t.latestRevision.updated_at).toLocaleDateString()
                      : new Date(t.created_at).toLocaleDateString()}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Th({ children, align = 'left' }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: '10px 20px',
        fontSize: 12,
        fontWeight: 600,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {children}
    </th>
  )
}

function Td({ children, align = 'left', muted }) {
  return (
    <td
      style={{
        textAlign: align,
        padding: '14px 20px',
        color: muted ? colors.textMuted : colors.textPrimary,
        fontSize: muted ? 13 : 14,
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  )
}
